import { useEffect, useMemo, useState } from "react";
import {
  ApiClientError,
  fetchCampaignAds,
  fetchCampaigns,
  fetchGoogleCampaignAds,
  fetchGoogleCampaigns,
  setAdStatus,
  setGoogleCampaignBudget,
  setGoogleCampaignStatus,
} from "../api/client";
import { StateBanner } from "../components/StateBanner";
import { ChannelTabs } from "../components/ChannelTabs";
import { PeriodSwitcher } from "../components/PeriodSwitcher";
import { Badge } from "../components/Badge";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { CampaignAdsPanel, type AdsFetchState } from "../components/CampaignAdsPanel";
import { GoogleOpsCard } from "../components/GoogleOpsCard";
import {
  PLATFORM_META,
  PLATFORM_TABS,
  type PlatformFilter,
  campaignStatusMeta,
  channelMeta,
  googleChannelTypeLabel,
  googlePrimaryStatusMeta,
  googleStrengthMeta,
  yen,
} from "../lib/meta";
import { usePeriod } from "../lib/usePeriod";
import type { AdPlatform, Campaign, ChannelFilter, GoogleAccountCheck } from "../types/contract";

/**
 * 媒体ごとの取得状態。Meta と Google は別APIなので独立して読込/失敗する
 * （片方が落ちてももう片方は描画する・失敗は媒体名付きバナーで出す）。
 */
type PlatformFetchState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ok"; data: Campaign[]; account?: GoogleAccountCheck };

/** 日予算の月換算（Google の月間上限の考え方: 日予算×30.4）。 */
const MONTHLY_FACTOR = 30.4;

const PLATFORM_STORAGE_KEY = "stoq-ad-console:campaigns-platform:v1";

function readStoredPlatform(): PlatformFilter {
  try {
    const v = localStorage.getItem(PLATFORM_STORAGE_KEY);
    return v === "meta" || v === "google" || v === "all" ? v : "all";
  } catch {
    return "all";
  }
}

function platformOf(c: Campaign): AdPlatform {
  return c.platform === "google" ? "google" : "meta";
}

export function CampaignsPage() {
  const { period, customStart, customEnd, range, setPeriod, setCustomStart, setCustomEnd } = usePeriod();
  const [platform, setPlatform] = useState<PlatformFilter>(readStoredPlatform);
  const [channel, setChannel] = useState<ChannelFilter>("all");
  const [search, setSearch] = useState("");
  const [metaState, setMetaState] = useState<PlatformFetchState>({ status: "idle" });
  const [googleState, setGoogleState] = useState<PlatformFetchState>({ status: "idle" });
  const [pendingAction, setPendingAction] = useState<{
    campaign: Campaign;
    action: "pause" | "activate";
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Google 日予算の変更（塩田さん推奨「予算アロケーション」をコンソールで管理）: 行内で編集 → 確認 → POST。
  const [budgetEdit, setBudgetEdit] = useState<{ id: string; value: string } | null>(null);
  const [pendingBudget, setPendingBudget] = useState<{ campaign: Campaign; value: number } | null>(null);
  const [budgetBusy, setBudgetBusy] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  // 「配信中の広告」展開パネル: 単一展開のみ（別行クリックで切替・再クリックで閉じる）。
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adsState, setAdsState] = useState<Record<string, AdsFetchState>>({});

  const changePlatform = (p: PlatformFilter) => {
    setPlatform(p);
    setExpandedId(null);
    try {
      localStorage.setItem(PLATFORM_STORAGE_KEY, p);
    } catch {
      /* 保存できなくても動作には影響しない */
    }
  };

  const loadAds = (campaign: Campaign) => {
    const key = campaign.id;
    setAdsState((prev) => ({ ...prev, [key]: { status: "loading" } }));
    const req =
      platformOf(campaign) === "google"
        ? fetchGoogleCampaignAds(campaign.id, { range, channel: campaign.channel })
        : fetchCampaignAds(campaign.id, { range, channel: campaign.channel });
    req
      .then((res) => setAdsState((prev) => ({ ...prev, [key]: { status: "ok", data: res.ads } })))
      .catch((e: unknown) => {
        const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
        setAdsState((prev) => ({ ...prev, [key]: { status: "error", message } }));
      });
  };

  const toggleAds = (campaign: Campaign) => {
    if (expandedId === campaign.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(campaign.id);
    loadAds(campaign); // 期間が変わっている可能性があるため展開の都度取得し直す
  };

  const load = () => {
    const wantMeta = platform === "all" || platform === "meta";
    const wantGoogle = platform === "all" || platform === "google";
    if (wantMeta) {
      setMetaState({ status: "loading" });
      fetchCampaigns(channel, search, range)
        .then((res) => setMetaState({ status: "ok", data: res.campaigns }))
        .catch((e: unknown) => {
          const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
          setMetaState({ status: "error", message });
        });
    } else {
      setMetaState({ status: "idle" });
    }
    if (wantGoogle) {
      setGoogleState({ status: "loading" });
      fetchGoogleCampaigns(channel, search, range)
        .then((res) => setGoogleState({ status: "ok", data: res.campaigns, account: res.account }))
        .catch((e: unknown) => {
          const message = e instanceof ApiClientError ? e.message : "予期しないエラーが発生しました。";
          setGoogleState({ status: "error", message });
        });
    } else {
      setGoogleState({ status: "idle" });
    }
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, channel, search, range.from, range.to]);

  // 媒体横断の表示行: Meta → Google の順（同一媒体内はAPIの並び順を維持）。
  const rows = useMemo(() => {
    const out: Campaign[] = [];
    if (metaState.status === "ok") out.push(...metaState.data);
    if (googleState.status === "ok") out.push(...googleState.data);
    return out;
  }, [metaState, googleState]);

  const anyLoading = metaState.status === "loading" || googleState.status === "loading";
  const allSettled = metaState.status !== "loading" && googleState.status !== "loading";
  const errors = [
    metaState.status === "error" ? { platform: "meta" as const, message: metaState.message } : null,
    googleState.status === "error" ? { platform: "google" as const, message: googleState.message } : null,
  ].filter((e): e is { platform: AdPlatform; message: string } => e !== null);
  const everythingFailed = allSettled && errors.length > 0 && rows.length === 0 && (platform !== "all" || errors.length === 2);

  const openConfirm = (campaign: Campaign, action: "pause" | "activate") => {
    setActionError(null);
    setPendingAction({ campaign, action });
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const { campaign, action } = pendingAction;
    const isGoogle = platformOf(campaign) === "google";
    if (!isGoogle && !campaign.ad_id) return;
    setActionBusy(true);
    setActionError(null);
    try {
      if (isGoogle) await setGoogleCampaignStatus(campaign.id, action);
      else await setAdStatus(campaign.ad_id as string, action);
      setPendingAction(null);
      load();
    } catch (e) {
      setActionError(e instanceof ApiClientError ? e.message : "操作に失敗しました。");
    } finally {
      setActionBusy(false);
    }
  };

  const runBudget = async () => {
    if (!pendingBudget) return;
    setBudgetBusy(true);
    setBudgetError(null);
    try {
      await setGoogleCampaignBudget(pendingBudget.campaign.id, pendingBudget.value);
      setPendingBudget(null);
      setBudgetEdit(null);
      load();
    } catch (e) {
      setBudgetError(e instanceof ApiClientError ? e.message : "予算の変更に失敗しました。");
    } finally {
      setBudgetBusy(false);
    }
  };

  const submitBudgetEdit = (campaign: Campaign) => {
    if (!budgetEdit) return;
    const value = Number(budgetEdit.value.replace(/[,，¥\s]/g, ""));
    if (!Number.isInteger(value) || value < 100) {
      setBudgetError("日予算は100円以上の整数で入力してください。");
      return;
    }
    setBudgetError(null);
    setPendingBudget({ campaign, value });
  };

  const expandedCampaign = expandedId ? rows.find((c) => c.id === expandedId) ?? null : null;
  const googleAccount = googleState.status === "ok" ? googleState.account : undefined;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">キャンペーン</h1>
        <PeriodSwitcher
          period={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomStartChange={setCustomStart}
          onCustomEndChange={setCustomEnd}
        />
        <input
          className="search-input"
          placeholder="キャンペーン名で検索"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="platform-row">
        <div className="tab-row" role="tablist" aria-label="媒体">
          {PLATFORM_TABS.map((id) => (
            <button
              key={id}
              role="tab"
              aria-selected={platform === id}
              className={`tab-btn ${platform === id ? "active" : ""}`}
              onClick={() => changePlatform(id)}
            >
              {PLATFORM_META[id].label}
            </button>
          ))}
        </div>
        <span className="platform-note">
          Google広告は 2026-10-14 配信開始。キャンペーン単位で一時停止/再開できます（予算・入札はGoogle広告の管理画面で）。
        </span>
      </div>
      <div style={{ marginBottom: 12 }}>
        <ChannelTabs value={channel} onChange={setChannel} />
      </div>

      {errors.length > 0 && !everythingFailed && (
        <div className="partial-errors">
          {errors.map((e) => (
            <StateBanner
              key={e.platform}
              kind="error"
              message={`${PLATFORM_META[e.platform].label}広告の取得に失敗しました: ${e.message}`}
              onRetry={load}
            />
          ))}
        </div>
      )}
      {everythingFailed && (
        <StateBanner
          kind="error"
          message={errors.map((e) => `${PLATFORM_META[e.platform].label}: ${e.message}`).join(" / ")}
          onRetry={load}
        />
      )}

      {platform !== "meta" && <GoogleOpsCard campaigns={rows} account={googleAccount} monthlyFactor={MONTHLY_FACTOR} />}

      {anyLoading && rows.length === 0 && (
        <div className="table-wrap">
          <div style={{ padding: 16 }} className="skeleton">
            &nbsp;
          </div>
        </div>
      )}
      {allSettled && !everythingFailed && rows.length === 0 && (
        <div className="empty-state">該当するキャンペーンはありません。</div>
      )}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>キャンペーン名</th>
                <th>媒体</th>
                <th>チャネル</th>
                <th>ステータス</th>
                <th>日予算</th>
                <th>広告費</th>
                <th>clicks</th>
                <th>CTR</th>
                <th>CPC</th>
                <th>成果</th>
                <th>主KPI単価</th>
                <th>購入額</th>
                {/* 2026-09-15 B′: 楽天CAPI RakutenPurchase（カスタムCV）。標準purchaseとは別集計・全チャネル共通（Googleは常に—） */}
                <th>楽天購入</th>
                <th>楽天購入額</th>
                <th>ROAS</th>
                <th>開始日</th>
                <th>操作</th>
                <th>広告</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const pf = platformOf(c);
                const pfMeta = PLATFORM_META[pf];
                const chMeta = channelMeta(c.channel);
                const stMeta = campaignStatusMeta(c.status);
                const gPrimary = pf === "google" ? googlePrimaryStatusMeta(c.google?.primary_status) : null;
                const isActive = c.status === "ACTIVE";
                const canOperate = c.operable && (pf === "google" || !!c.ad_id);
                return (
                  <tr key={`${pf}:${c.id}`}>
                    <td style={{ whiteSpace: "normal", minWidth: 220 }}>
                      {c.name}
                      {pf === "google" && (
                        <span className="cell-sub">
                          {googleChannelTypeLabel(c.google?.channel_type)}
                          {c.google?.end ? ` · 終了 ${c.google.end}` : ""}
                        </span>
                      )}
                    </td>
                    <td>
                      <Badge label={pfMeta.label} color={pfMeta.c} bg={pfMeta.bg} />
                    </td>
                    <td>
                      <Badge label={chMeta.label} color={chMeta.c} bg={chMeta.bg} />
                    </td>
                    <td>
                      <span className="badge-stack">
                        <Badge label={stMeta.label} color={stMeta.c} bg={stMeta.bg} />
                        {gPrimary && <Badge label={gPrimary.label} color={gPrimary.c} bg={gPrimary.bg} />}
                        {pf === "google" && c.google?.ops?.learning.active && (
                          <Badge
                            label={`学習期間中 残${c.google.ops.learning.days_left ?? "?"}日`}
                            color="var(--st-pending-c)"
                            bg="var(--st-pending-bg)"
                          />
                        )}
                        {pf === "google" && c.google?.ops && (() => {
                          const s = googleStrengthMeta(c.google.ops.ad_strength.min);
                          return <Badge label={s.label} color={s.c} bg={s.bg} />;
                        })()}
                      </span>
                    </td>
                    <td>
                      {pf === "google" && budgetEdit?.id === c.id ? (
                        <span className="budget-edit">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={budgetEdit.value}
                            aria-label="日予算（円）"
                            onChange={(e) => setBudgetEdit({ id: c.id, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") submitBudgetEdit(c);
                              if (e.key === "Escape") setBudgetEdit(null);
                            }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => submitBudgetEdit(c)}>
                            保存
                          </button>
                          <button className="link-btn" onClick={() => { setBudgetEdit(null); setBudgetError(null); }}>
                            取消
                          </button>
                          {budgetError && <span className="ad-card-copy-failed">{budgetError}</span>}
                        </span>
                      ) : (
                        <span className="budget-cell">
                          {c.daily_budget != null ? yen(c.daily_budget) : "—"}
                          {pf === "google" && c.operable && (
                            <button
                              className="link-btn"
                              onClick={() => { setBudgetError(null); setBudgetEdit({ id: c.id, value: String(c.daily_budget ?? "") }); }}
                              title="日予算を変更（Google広告のキャンペーン予算を書き換えます）"
                            >
                              変更
                            </button>
                          )}
                        </span>
                      )}
                    </td>
                    <td>{yen(c.spend)}</td>
                    <td>{c.clicks.toLocaleString("ja-JP")}</td>
                    <td>{c.ctr}</td>
                    <td>{c.cpc != null ? yen(c.cpc) : "—"}</td>
                    <td>
                      {c.primary.count.toLocaleString("ja-JP")}件（{c.primary.label}）
                    </td>
                    <td>{c.primary.value != null ? yen(c.primary.value) : "—"}</td>
                    <td>{c.purchase_value > 0 ? yen(c.purchase_value) : "—"}</td>
                    <td>{c.rakuten_purchases > 0 ? `${c.rakuten_purchases.toLocaleString("ja-JP")}件` : "—"}</td>
                    <td>{c.rakuten_purchase_value > 0 ? yen(c.rakuten_purchase_value) : "—"}</td>
                    <td>{c.roas != null ? `${c.roas.toFixed(2)}x` : "—"}</td>
                    <td>{c.start ?? "—"}</td>
                    <td>
                      {canOperate ? (
                        <button
                          className="btn btn-sm"
                          onClick={() => openConfirm(c, isActive ? "pause" : "activate")}
                          title={pf === "google" ? "キャンペーン本体の配信ステータスを変更します" : undefined}
                        >
                          {isActive ? "一時停止" : "再開"}
                        </button>
                      ) : (
                        <span className="text-faint" style={{ color: "var(--ink-faint)", fontSize: 11.5 }}>—</span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => toggleAds(c)}>
                        {expandedId === c.id ? "広告を閉じる" : "広告を見る"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {expandedId && (
        <div className="ads-panel-wrap">
          <div className="ads-panel-header">
            <span className="ads-panel-campaign-name">
              {expandedCampaign && (
                <Badge
                  label={PLATFORM_META[platformOf(expandedCampaign)].label}
                  color={PLATFORM_META[platformOf(expandedCampaign)].c}
                  bg={PLATFORM_META[platformOf(expandedCampaign)].bg}
                />
              )}{" "}
              {expandedCampaign?.name ?? expandedId}
            </span>
            <button className="btn btn-sm" onClick={() => setExpandedId(null)}>
              閉じる
            </button>
          </div>
          <CampaignAdsPanel
            state={adsState[expandedId] ?? { status: "loading" }}
            onRetry={() => expandedCampaign && loadAds(expandedCampaign)}
            onChanged={() => expandedCampaign && loadAds(expandedCampaign)}
          />
        </div>
      )}

      {pendingBudget && (
        <ConfirmDialog
          title="日予算を変更しますか？"
          description={`「${pendingBudget.campaign.name}」の日予算を ${
            pendingBudget.campaign.daily_budget != null ? yen(pendingBudget.campaign.daily_budget) : "—"
          } → ${yen(pendingBudget.value)}（月換算 約${yen(Math.round(pendingBudget.value * MONTHLY_FACTOR))}）に変更します。${
            pendingBudget.campaign.google?.ops?.learning.active
              ? ` ⚠ 学習期間中です（残り${pendingBudget.campaign.google.ops.learning.days_left ?? "?"}日）。大きな変更は学習をリセットするおそれがあります。`
              : ""
          }`}
          confirmLabel="変更する"
          danger={!!pendingBudget.campaign.google?.ops?.learning.active}
          onConfirm={runBudget}
          onCancel={() => setPendingBudget(null)}
          busy={budgetBusy}
          errorMessage={budgetError}
        />
      )}

      {pendingAction && (
        <ConfirmDialog
          title={pendingAction.action === "pause" ? "配信を一時停止しますか？" : "配信を再開しますか？"}
          description={`「${pendingAction.campaign.name}」${
            platformOf(pendingAction.campaign) === "google" ? "（Google広告・キャンペーン本体）" : ""
          }の配信ステータスを${pendingAction.action === "pause" ? "一時停止" : "配信中"}に変更します。${
            platformOf(pendingAction.campaign) === "google" && pendingAction.action === "activate" && pendingAction.campaign.daily_budget != null
              ? ` 日予算 ${yen(pendingAction.campaign.daily_budget)}（月換算 約${yen(Math.round(pendingAction.campaign.daily_budget * MONTHLY_FACTOR))}）で配信されます。開始後約2週間は学習期間として設定変更を静観してください。`
              : ""
          }`}
          confirmLabel={pendingAction.action === "pause" ? "一時停止する" : "再開する"}
          danger={pendingAction.action === "pause"}
          onConfirm={runAction}
          onCancel={() => setPendingAction(null)}
          busy={actionBusy}
          errorMessage={actionError}
        />
      )}
    </div>
  );
}
